import CloseIcon from '@mui/icons-material/Close'
import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { sendSupportSessionCompletionReminder } from 'src/supportPortal/services/supportBookedEventActionsService'

// Support nudges the practitioner who started but never completed a session, over WhatsApp and/or
// email. The message carries a deep link to open and finish the session (backend builds it); WhatsApp
// uses the 24h warm-window free-form path with a Twilio-template opener fallback outside it.
export default function SupportSessionCompletionReminderDialog({ open, onClose, booking, onSent }) {
  const [whatsapp, setWhatsapp] = useState(true)
  const [email, setEmail] = useState(true)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (open) {
      setWhatsapp(true)
      setEmail(true)
      setSending(false)
    }
  }, [open])

  const practitionerName = booking?.mentorName || 'the practitioner'
  const patientName = booking?.patientInfoData?.name || booking?.patientName || 'the patient'

  async function handleSend() {
    const modes = [whatsapp ? 'whatsapp' : null, email ? 'email' : null].filter(Boolean)
    if (!modes.length) {
      toast.error('Pick at least one channel')
      return
    }
    if (!booking?.id) {
      toast.error('This booking is missing an id')
      return
    }
    setSending(true)
    const result = await sendSupportSessionCompletionReminder(booking.id, modes)
    setSending(false)
    if (result.ok) {
      toast.success(`Reminder sent to ${practitionerName}`)
      onSent?.(result)
      onClose?.()
      return
    }
    toast.error(result.message || 'Could not send the reminder.')
  }

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
          color: '#fff',
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <NotificationsActiveRoundedIcon sx={{ fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Remind to complete
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.5, wordBreak: 'break-word' }}>
              {practitionerName}
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={sending} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={1.5}>
          <Alert severity="warning" sx={{ borderRadius: 2 }} icon={false}>
            This session (with {patientName}) was started but never marked complete. We&apos;ll send{' '}
            {practitionerName} a message with a link to open and finish it.
          </Alert>

          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
            SEND VIA
          </Typography>
          <Box
            sx={{
              borderRadius: 2,
              border: '1px solid rgba(148, 163, 184, 0.24)',
              px: 1.5,
              py: 0.5,
            }}
          >
            <FormControlLabel
              control={<Checkbox checked={whatsapp} onChange={(e) => setWhatsapp(e.target.checked)} />}
              label={
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <WhatsAppIcon sx={{ fontSize: 18, color: '#128c7e' }} />
                  <span>WhatsApp</span>
                </Stack>
              }
            />
            <FormControlLabel
              control={<Checkbox checked={email} onChange={(e) => setEmail(e.target.checked)} />}
              label={
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <EmailRoundedIcon sx={{ fontSize: 18, color: '#4f46e5' }} />
                  <span>Email</span>
                </Stack>
              }
            />
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(15, 23, 42, 0.55)' }}>
            WhatsApp uses an approved template outside the 24-hour window and a direct message inside it.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={sending} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          disabled={sending || (!whatsapp && !email)}
          variant="contained"
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />}
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            backgroundColor: '#d97706',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#b45309', boxShadow: 'none' },
          }}
        >
          {sending ? 'Sending…' : 'Send reminder'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
